<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('multistop.rearrangeStops')"
    class="rearrange-stops-modal"
    @keypress.enter="onConfirm"
    @keypress.esc="onCancel"
  >
    <p>
      <span>{{ t('multistop.rearrangeStopsInstructions') }}</span>
      <span v-if="isExistingShipment" :style="{ 'margin-left': 'var(--sp-1)' }">
        {{ t('multistop.rearrangeStopsExecutionWarning') }}
      </span>
    </p>

    <ArrangeableShipmentStopList
      id="arrangeable-stop-list"
      v-model:shipment="internalShipment"
      tabindex="-1"
    />

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="
          // VSCode's syntax higlighting gets borked with instanceof in templates
          shipment instanceof ProposedShipment
        "
        mode="tertiary"
        :icon="PrimeIcons.PLUS"
        :label="t('common.addParam', { param: t('shipments.stops') })"
        @click="emit('add-stop', { shipmentIdentifier: shipment.identifier })"
      />
      <RygenButton
        mode="primary"
        :icon="PrimeIcons.SORT_ALT"
        :label="t('common.confirm')"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Shipment } from '@/model';
import {
  RequestAddStopToShipmentPayload,
  ProposedShipment,
} from '@/model/routeBuilder';

import ArrangeableShipmentStopList, {
  ArrangeableShipment,
} from '@/components/multistop/ArrangeableShipmentStopList.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipment: {
    type: Object as PropType<ArrangeableShipment>,
    default: () => new ProposedShipment(),
  },
});
const { shipment } = toRefs(props);
const emit = defineEmits<{
  (event: 'add-stop', payload: RequestAddStopToShipmentPayload): void;
  (event: 'cancel'): void;
  (event: 'confirm', shipment: ArrangeableShipment): void;
}>();

const { t } = useI18n();

const Clazz = ref(Object.getPrototypeOf(shipment.value).constructor);
const internalShipment: Ref<ArrangeableShipment> = ref(
  new Clazz.value(shipment.value),
);

const isExistingShipment = computed(() => {
  return (
    internalShipment.value instanceof Shipment && !!internalShipment.value.id
  );
});

function onConfirm() {
  isVisible.value = false;
  emit('confirm', internalShipment.value);
}

function onCancel() {
  isVisible.value = false;
  emit('cancel');
}

watch(
  shipment,
  (newVal: ArrangeableShipment) => {
    Clazz.value = Object.getPrototypeOf(newVal).constructor;
    internalShipment.value = new Clazz.value(newVal);
  },
  { deep: true },
);

watch(isVisible, () => {
  internalShipment.value = new Clazz.value(shipment.value);
});
</script>

<style>
.rearrange-stops-modal.rygen-modal.p-dialog.p-component {
  --rygen-modal-max-width: 100%;

  .proposed-shipment-stop-list {
    margin-top: var(--sp-6);
  }
}
</style>
